import { CheckCircleOutlined, CloseCircleOutlined, TagOutlined } from "@ant-design/icons";
import { Alert, Button, Input } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useState } from "react";
import useAppStore from "@/store/useAppStore";
import type { PromoCodeResult } from "@/types";
import { safeStringify } from "@/utils/api";

interface PromoCodesProps {
  isLoading: boolean;
}

const PromoCodes: React.FC<PromoCodesProps> = ({ isLoading }) => {
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState<PromoCodeResult | null>(null);

  const applyPromoCodeWrapper = useAppStore((state) => state.applyPromoCodeWrapper);
  const clearPromoCode = useAppStore((state) => state.clearPromoCode);
  const { appliedPromoCode } = useAppStore();

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;
    try {
      const result = await applyPromoCodeWrapper(promoCode);
      setPromoResult(result);
    } catch (error) {
      console.error("Error applying promo code:", safeStringify(error));
      setPromoResult({
        success: false,
        code: "",
        type: "fixed",
        value: 0,
        creditsAdded: 0,
        discountPercentage: 0,
        freeQuizzes: 0,
        description: "An error occurred. Please try again.",
      });
    }
  };

  const handleClearPromoCode = async () => {
    await clearPromoCode();
    setPromoCode("");
    setPromoResult(null);
  };

  const formatResultMessage = (result: PromoCodeResult): string => {
    if (!result.success) return result.description;
    let message = result.description || "Promo code applied!";
    if (result.type === "percentage") {
      message += ` ${result.discountPercentage}% discount.`;
    } else if (result.type === "fixed") {
      message += ` $${result.value} off.`;
    } else if (result.type === "free_credits") {
      message += ` ${result.creditsAdded} free credits.`;
    } else if (result.type === "free_quizzes") {
      message += ` ${result.freeQuizzes} free quizzes.`;
    }
    return message;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApplyPromoCode();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <TagOutlined className="text-brand-600" />
        <h3 className="text-sm font-semibold text-gray-900">Promo Code</h3>
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Enter code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="flex-1 h-10 rounded-lg"
          style={{ textTransform: "uppercase" }}
        />
        <Button
          type="primary"
          onClick={handleApplyPromoCode}
          loading={isLoading}
          disabled={!promoCode.trim()}
          className="h-10 px-4 rounded-lg font-medium"
        >
          Apply
        </Button>
      </div>

      {appliedPromoCode && appliedPromoCode.type !== "free_credits" && (
        <button
          onClick={handleClearPromoCode}
          className="mt-3 text-sm text-red-500 hover:text-red-600 font-medium"
        >
          Clear promo code
        </button>
      )}

      <AnimatePresence>
        {promoResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="mt-4"
          >
            <div
              className={`p-3 rounded-lg text-sm ${
                promoResult.success
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              <div className="flex items-start gap-2">
                {promoResult.success ? (
                  <CheckCircleOutlined className="mt-0.5 text-green-500" />
                ) : (
                  <CloseCircleOutlined className="mt-0.5 text-red-500" />
                )}
                <span>{formatResultMessage(promoResult)}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PromoCodes;
